import { useState, useCallback } from 'react';
import { useParams, useLocation, Link } from 'wouter';
import { Trash2, ArrowLeft, AlertCircle, Tag, X, Check, FolderOpen } from 'lucide-react';
import { useGetArtefact, useDeleteArtefact, useGetActivity, useGetProjects, customInstance } from '@edison/api-client-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Card } from '@/components/ui/card.js';
import { MarkdownContent } from '@/components/ui/markdown-content.js';
import { formatLabel } from '@/lib/utils.js';

type ArtefactWithTag = {
  id: string;
  title: string;
  type: string;
  content: string;
  createdAt: string;
  projectId?: string | null;
  activityId?: string | null;
  activityTagSource?: 'ai' | 'manual' | null;
  activityTagConfidence?: string | null;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{3,4}-[0-9a-f]{12}$/i;

function ActivityTagSection({ artefact }: { artefact: ArtefactWithTag }) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const qc = useQueryClient();

  const { data: activityData } = useGetActivity(artefact.activityId ?? '');
  const activity = activityData?.data;

  const tagMutation = useMutation({
    mutationFn: (activityId: string | null) =>
      customInstance<{ data: ArtefactWithTag }>({
        url: `/api/artefacts/${artefact.id}/tag-activity`,
        method: 'PATCH',
        data: { activityId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['artefacts', artefact.id] });
      setEditing(false);
      setInputValue('');
    },
  });

  const handleSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (!UUID_RE.test(trimmed)) {
      setValidationError('Please enter a valid activity UUID');
      return;
    }
    setValidationError('');
    tagMutation.mutate(trimmed);
  }, [inputValue, tagMutation]);

  const handleRemove = () => {
    tagMutation.mutate(null);
  };

  return (
    <Card variant="static" className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium flex items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Activity Tag
        </h2>
      </div>

      {artefact.activityId ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium">
              {activity ? activity.name : artefact.activityId}
            </span>
            {artefact.activityTagSource === 'ai' && (
              <span className="rounded-full bg-violet-100 text-violet-700 px-2 py-0.5 text-xs font-medium">
                AI tagged
                {artefact.activityTagConfidence !== null && artefact.activityTagConfidence !== undefined
                  ? ` · ${parseFloat(artefact.activityTagConfidence).toFixed(0)}% confidence`
                  : ''}
              </span>
            )}
            {artefact.activityTagSource === 'manual' && (
              <Badge variant="positive">Manually tagged</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => { setEditing(true); setInputValue(''); }}
            >
              Change tag
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={tagMutation.isPending}
              className="text-muted-foreground hover:text-destructive"
              aria-label="Remove activity tag"
            >
              <X className="h-3 w-3" aria-hidden="true" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-xs text-muted-foreground mb-2">No activity tag assigned.</p>
          {!editing && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              <Tag className="h-3 w-3" aria-hidden="true" />
              Add tag
            </Button>
          )}
        </div>
      )}

      {editing && (
        <div className="flex items-center gap-2 pt-1">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setValidationError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') { setEditing(false); setValidationError(''); } }}
            placeholder="Activity UUID"
            className="flex-1 rounded-lg border bg-background px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label="Activity ID"
          />
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={tagMutation.isPending || !inputValue.trim()}
            aria-label="Confirm activity tag"
          >
            <Check className="h-3 w-3" aria-hidden="true" />
            {tagMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setEditing(false); setInputValue(''); setValidationError(''); }}
            aria-label="Cancel"
          >
            Cancel
          </Button>
        </div>
      )}

      {validationError && (
        <p className="text-xs text-destructive mt-1">{validationError}</p>
      )}

      {tagMutation.isError && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          Failed to update tag. Please try again.
        </p>
      )}
    </Card>
  );
}

// Artefact types that can contain markdown or readable prose — render with formatting.
const MARKDOWN_TYPES = new Set(['llm_session', 'meeting_transcript', 'manual_note', 'prd_document', 'market_search', 'file_upload', 'abandoned_work']);

function ArtefactContent({ type, content }: { type: string; content: string }) {
  if (MARKDOWN_TYPES.has(type)) {
    return (
      <Card variant="static" className="min-h-[4rem]">
        <MarkdownContent>{content}</MarkdownContent>
      </Card>
    );
  }
  // GitHub artefacts (commits, PRs, issues): preserve mono formatting for diffs/messages
  return (
    <Card variant="static" className="whitespace-pre-wrap text-sm font-mono leading-relaxed overflow-x-auto">
      {content}
    </Card>
  );
}

export function ArtefactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data, isLoading, error } = useGetArtefact(id ?? '');
  const { mutate: deleteArtefact, isPending: isDeleting } = useDeleteArtefact();
  const { data: projectsData } = useGetProjects();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded-lg skeleton" />
        <div className="h-4 w-48 rounded-lg skeleton" />
        <div className="h-48 w-full rounded-lg skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Failed to load artefact.</span>
      </div>
    );
  }

  const artefact = data?.data as ArtefactWithTag | undefined;
  const projectById = new Map((projectsData?.data?.items ?? []).map((p) => [p.id, p.name]));

  if (!artefact) {
    return <p className="text-muted-foreground">Artefact not found.</p>;
  }

  const projectName = artefact.projectId ? (projectById.get(artefact.projectId) ?? null) : null;

  const handleDelete = () => {
    deleteArtefact(artefact.id, {
      onSuccess: () => navigate('/artefacts'),
    });
  };

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link
            href="/artefacts"
            className="mb-2 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Artefacts
          </Link>
          <h1 className="text-xl font-semibold">{artefact.title}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="default">{formatLabel(artefact.type)}</Badge>
            <span className="text-xs text-muted-foreground">
              {new Date(artefact.createdAt).toLocaleDateString('en-AU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </span>
            {projectName && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <FolderOpen className="h-3 w-3" aria-hidden="true" />
                {projectName}
              </span>
            )}
          </div>
        </div>

        <div>
          {!confirmDelete ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              className="text-muted-foreground hover:text-destructive"
              aria-label="Delete artefact"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
              <p className="text-sm text-destructive">Delete this artefact?</p>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Confirm'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>

      <ActivityTagSection artefact={artefact} />

      <ArtefactContent type={artefact.type} content={artefact.content} />
    </div>
  );
}
